import React from 'react';

import { Link } from 'react-router-dom';

import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { ChannelMarkupTesterPanelProps } from './ChannelMarkupTesterPanel.types';

type Props = Pick<
  ChannelMarkupTesterPanelProps,
  | 'testerValues'
  | 'registerTester'
  | 'setTesterValue'
  | 'subscriberTierOptions'
  | 'userRoleOptions'
  | 'audienceSearchInput'
  | 'setAudienceSearchInput'
  | 'audienceSearchEnabled'
  | 'audienceOptions'
  | 'isAudienceSearchLoading'
  | 'noAudienceResults'
>;

export default function ChannelMarkupEvaluationContextSection({
  testerValues,
  registerTester,
  setTesterValue,
  subscriberTierOptions,
  userRoleOptions,
  audienceSearchInput,
  setAudienceSearchInput,
  audienceSearchEnabled,
  audienceOptions,
  isAudienceSearchLoading,
  noAudienceResults,
}: Props) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'background.paper' }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            2. Evaluation context
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Defaults are prefilled so you can run the tester as-is for LPC loyalty offers. Adjust region, currency,
            audience, or offer inputs when needed.
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Preview region: {testerValues.context.region || 'AU'} · preview currency:{' '}
          {testerValues.context.currency || 'AUD'}
        </Typography>
        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
          <TextField fullWidth label="Offer ID" {...registerTester('context.offer.offerId')} />
          <TextField fullWidth label="Offer type" {...registerTester('context.offer.loyaltyOfferType')} />
        </Stack>
        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
          <TextField
            fullWidth
            label="Offer price"
            type="number"
            inputProps={{ step: '0.01' }}
            {...registerTester('context.offer.total')}
          />
          <TextField
            fullWidth
            label="Lux Plus offer price"
            type="number"
            inputProps={{ step: '0.01' }}
            {...registerTester('context.offer.luxPlusPrice')}
          />
        </Stack>
        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
          <TextField fullWidth select label="Subscriber tier" {...registerTester('context.subscriberTier')}>
            <MenuItem value="">None</MenuItem>
            {subscriberTierOptions.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.displayName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label="User role"
            value={testerValues.context.roles[0] ?? ''}
            onChange={(event) => setTesterValue('context.roles', event.target.value ? [event.target.value] : [])}
          >
            <MenuItem value="">None</MenuItem>
            {userRoleOptions.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.displayName}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <TextField
          fullWidth
          label="Promo audience"
          helperText="Search promo audiences to validate this markup against a real audience."
          value={audienceSearchInput}
          onChange={(event) => setAudienceSearchInput(event.target.value)}
          inputProps={{ 'data-testid': 'promo-audience-input' }}
          InputProps={{
            endAdornment: isAudienceSearchLoading ? (
              <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
            ) : null,
          }}
        />
        {audienceSearchEnabled ? (
          <Stack spacing={1}>
            {audienceOptions.length > 0 ? (
              <List dense disablePadding>
                {audienceOptions.map((audience) => (
                  <ListItemButton key={audience.audienceId} divider disabled sx={{ cursor: 'default' }}>
                    <ListItemText
                      primary={audience.audienceName}
                      secondary={[audience.audienceId, audience.audienceType, audience.syncModel]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No audiences found
              </Typography>
            )}
          </Stack>
        ) : null}
        {noAudienceResults ? (
          <Button component={Link} to="/marketing/audiences/create" variant="text">
            Create a new Audience
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}
